import type { Market } from "./Market";
import type { MediaModel } from "./Media";
import type { Merchant } from "./Merchant";
import type { Equipment } from "./Equipment";
import type { Status } from "./Status";
import type { User } from "./User";

export type EventSubtype = {
  id: number;
  name: string;
  event_type_id: number;
  created_at?: string;
  updated_at?: string;
};

export type EventEquipmentOption = {
  id: number;
  name: string;
  reference?: string | null;
  serial_number?: string | null;
  registration_number?: string | null;
  equipment_type?: {
    id?: number | null;
    name?: string | null;
    code?: string | null;
  } | null;
  assigned_to?: {
    type?: string | null;
    id?: number | null;
    label?: string | null;
    market_id?: number | null;
    merchant_id?: number | null;
    company_id?: number | null;
  } | null;
  maintenance_site?: {
    type?: string | null;
    id?: number | null;
    label?: string | null;
  } | null;
  organilog_equipment_id?: string | number | null;
};

export type EventAssignedUser = {
  id: number;
  name?: string | { fr?: string };
  email?: string;
  [key: string]: unknown;
};

export type Event = {
  id: number;
  event_type_id: number;
  event_date: string;
  title: string;
  description: string;
  user_id: number;
  status: number;
  workflow_status?: number | null;
  is_urgent?: boolean;
  created_at: string;
  updated_at: string | null;
  /** Réécriture de la description uniquement — null si le constat d'origine n'a jamais changé. */
  description_updated_at?: string | null;
  event_subtype_id?: number | null;
  event_subtype?: EventSubtype | null;
  infraction_type_id?: number | null;
  market_id: number;
  merchant_id: number | null;
  event_type?: EventType;
  user?: User;
  assigned?: EventAssignedUser[];
  is_exportable: boolean;
  statut?: Status;
  market?: Market;
  linked_merchant?: Merchant;
  media?: MediaModel[];
  infraction_type?: EventSubtype | null;
  incident_type?: EventSubtype | null;
  equipment_id?: number | null;
  equipment?: Partial<Equipment> | null;
  equipments?: Equipment[];
  equipment_not_repertoried?: boolean;
  organilog_intervention_id?: string | number | null;
  organilog_intervention_url?: string | null;
  ocv_event_url?: string | null;
};
export interface EventReply {
  id?: number;
  created_at?: Date;
  event_id: number;
  event_type_id: number;
  reply: string;
  status: number;
  workflow_status?: number | null;
  is_urgent?: boolean;
  /** Réponse écrite par le produit et non par un humain : affichée « Système », non modifiable. */
  is_system?: boolean;
  updated_at?: Date;
  user_id: number;
}
export interface EventType {
  id: number;
  name: string;
  event_subtypes?: EventSubtype[];
  merchant_infraction_types?: EventSubtype[];
  incident_types?: EventSubtype[];
}

export type MerchantInfractionType = EventSubtype;
export interface TriggerCondition {
  operator: ConditionOperatorValue;
  value?: unknown;
  value2?: unknown;
  case_sensitive?: boolean;
}

export interface MarketDaySchedule {
  day_id: number; // 0=Sunday, 1=Monday, etc.
  start_time?: string; // Format: "HH:MM" - Optional, si absent = toute la journée
}

export enum ConditionOperator {
  EQUALS = "equals",
  NOT_EQUALS = "not_equals",
  CONTAINS = "contains",
  NOT_CONTAINS = "not_contains",
  STARTS_WITH = "starts_with",
  ENDS_WITH = "ends_with",
  GREATER_THAN = "greater_than",
  GREATER_OR_EQUAL = "greater_or_equal",
  LESS_THAN = "less_than",
  LESS_OR_EQUAL = "less_or_equal",
  BETWEEN = "between",
  IN = "in",
  NOT_IN = "not_in",
  IS_EMPTY = "is_empty",
  IS_NOT_EMPTY = "is_not_empty",
  IS_TRUE = "is_true",
  IS_FALSE = "is_false",
  MATCHES_REGEX = "matches_regex",
  IS_BEFORE = "is_before",
  IS_AFTER = "is_after",
  IS_TODAY = "is_today",
  IS_ON_MARKET_DAY = "is_on_market_day",
  IS_ON_MARKET_DAY_UNTIL = "is_on_market_day_until",
  IS_NOT_ON_MARKET_DAY = "is_not_on_market_day",
  FILE_EXISTS = "file_exists",
  FILE_COUNT = "file_count",
}

export type ConditionOperatorValue = `${ConditionOperator}`;
export interface ReportingConfig {
  reportType: string;
  reportTitle: string;
  description?: string;
  autoAssignTo?: string;
  includeFormPhotos: boolean;
  includeFormData: boolean;
  linkedMerchantField?: string;
  additionalData?: Record<string, unknown>;
}
export type ConditionMode = "AND" | "OR";

export interface OperatorMetadata {
  operator: ConditionOperatorValue;
  label: string;
  description?: string;
  requiresValue: boolean;
  requiresSecondValue?: boolean;
  supportedFieldTypes: string[]; // Types de champs supportés
  valueType?: "text" | "number" | "date" | "boolean" | "array" | "regex" | "market_days" | "market_day_schedule";
  icon?: string;
}
export const OPERATORS_BY_FIELD_TYPE: Record<string, ConditionOperatorValue[]>
  = {
    text: [
      "contains",
      "not_contains",
      "equals",
      "not_equals",
      "starts_with",
      "ends_with",
      "is_empty",
      "is_not_empty",
      "matches_regex",
    ],
    textInput: [
      "contains",
      "not_contains",
      "equals",
      "not_equals",
      "starts_with",
      "ends_with",
      "is_empty",
      "is_not_empty",
      "matches_regex",
    ],
    textarea: [
      "contains",
      "not_contains",
      "equals",
      "not_equals",
      "starts_with",
      "ends_with",
      "is_empty",
      "is_not_empty",
      "matches_regex",
    ],
    email: [
      "contains",
      "equals",
      "not_equals",
      "is_empty",
      "is_not_empty",
      "matches_regex",
    ],
    phone: [
      "contains",
      "equals",
      "not_equals",
      "starts_with",
      "is_empty",
      "is_not_empty",
    ],
    url: [
      "contains",
      "equals",
      "not_equals",
      "starts_with",
      "is_empty",
      "is_not_empty",
    ],
    number: [
      "equals",
      "not_equals",
      "greater_than",
      "greater_or_equal",
      "less_than",
      "less_or_equal",
      "between",
      "is_empty",
      "is_not_empty",
    ],
    number_group: [
      "equals",
      "not_equals",
      "greater_than",
      "greater_or_equal",
      "less_than",
      "less_or_equal",
      "between",
      "is_empty",
      "is_not_empty",
    ],
    decimal: [
      "equals",
      "not_equals",
      "greater_than",
      "greater_or_equal",
      "less_than",
      "less_or_equal",
      "between",
      "is_empty",
      "is_not_empty",
    ],
    select: [
      "equals",
      "not_equals",
      "in",
      "not_in",
      "is_empty",
      "is_not_empty",
    ],
    multi_select: [
      "contains",
      "not_contains",
      "in",
      "not_in",
      "is_empty",
      "is_not_empty",
    ],
    radio: ["equals", "not_equals", "in", "not_in"],
    boolean: ["is_true", "is_false"],
    checkbox: ["is_true", "is_false"],
    date: [
      "equals",
      "not_equals",
      "is_before",
      "is_after",
      "between",
      "is_today",
      "is_empty",
      "is_not_empty",
    ],
    datetime: [
      "equals",
      "not_equals",
      "is_before",
      "is_after",
      "between",
      "is_empty",
      "is_not_empty",
    ],
    time: [
      "equals",
      "not_equals",
      "is_before",
      "is_after",
      "between",
      "is_empty",
      "is_not_empty",
      "is_on_market_day",
      "is_on_market_day_until",
      "is_not_on_market_day",
    ],
    file: ["file_exists", "file_count"],
  };
export interface EventTrigger {
  enabled: boolean;
  eventTypeId: string;
  merchantInfractionTypeId?: string;
  conditions: TriggerCondition[];
  market_days_filter?: number[]; // Array of day IDs (0=Sunday, 1=Monday, etc.) for market day filtering
}

export interface EventTriggerService {
  validateTriggerConfiguration(trigger: EventTrigger): ValidationResult;
}
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}
export enum EventStatus {
  NEW = 34,
  URGENT = 28,
  CLOSED = 29,
  IN_PROGRESS = 27,

}
